import { useEffect, useState } from "react";
import { get } from "../api";
import type {
  CustomerRevenueBrief,
  RevenueDirection,
  RevenueFact,
  RevenueFinding,
  RevenueRef,
} from "../api";
import { Time } from "../Time";
import { money, monthLabel, monthParam } from "./format";

/**
 * A month in a few lines: the agent's headline and findings over the
 * deterministic digest's numbers.
 *
 * Every figure here is rendered from a fact the server computed — the
 * evidence chips, never the prose (invariant 51). The model chose what to
 * say; the chips are how a reader checks it.
 *
 * Before a month freezes there is no brief, only the digest's signals,
 * because a model's opinion of a month that is still moving would be
 * rewritten every day.
 */
export default function BriefCard({ slug, month }: { slug: string; month: string }) {
  const [data, setData] = useState<CustomerRevenueBrief | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSignals, setShowSignals] = useState(false);

  useEffect(() => {
    setData(null);
    setError(null);
    setShowSignals(false);
    void get<CustomerRevenueBrief>(`/api/customers/${slug}/revenue/${monthParam(month)}/brief`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [slug, month]);

  if (error) return <div className="error">{error}</div>;
  if (!data) return null;

  const { digest, brief } = data;
  if (digest.state === "not_computed") return null;
  const currency = digest.currency;
  const signals = digest.signals;

  return (
    <div className="rev-brief">
      <div className="rev-brief-head">
        <strong>{monthLabel(month)} in brief</strong>
        <span className={`badge dir ${brief?.direction ?? digest.direction}`}>
          {DIRECTION[brief?.direction ?? digest.direction]}
        </span>
      </div>

      {brief ? (
        <>
          <p className="rev-headline">{brief.headline}</p>
          <Findings kind="highlight" items={brief.highlights} currency={currency} />
          <Findings kind="lowlight" items={brief.lowlights} currency={currency} />
          {brief.open_questions.length > 0 && (
            <ul className="oq dim">
              {brief.open_questions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          )}
          <div className="dim rev-brief-foot">
            {data.model_called ? "Written" : "Quiet month — no model was called"}
            {data.created_at && (
              <>
                {" · "}
                <Time value={data.created_at} />
              </>
            )}
            {signals.length > 0 && (
              <>
                {" · "}
                <button className="btn tiny" onClick={() => setShowSignals((v) => !v)}>
                  {showSignals ? "Hide" : "Show"} the {signals.length} signal
                  {signals.length === 1 ? "" : "s"} it was written from
                </button>
              </>
            )}
          </div>
          {showSignals && <Signals data={data} />}
        </>
      ) : (
        <>
          <p className="dim rev-headline">{whyNoBrief(data)}</p>
          {signals.length > 0 ? (
            <Signals data={data} />
          ) : (
            <p className="dim">Nothing has moved enough to mention.</p>
          )}
        </>
      )}
    </div>
  );
}

const DIRECTION: Record<RevenueDirection, string> = {
  up: "Up",
  down: "Down",
  flat: "Flat",
  mixed: "Mixed",
};

function whyNoBrief(d: CustomerRevenueBrief): string {
  switch (d.digest.state) {
    case "live":
      return "The month is in progress, so these are run-rate signals only. The brief is written once it freezes.";
    case "pending":
      return "The month is over but not frozen yet. Its brief is written after it freezes on the 3rd.";
    default:
      return d.awaiting
        ? "Frozen; the brief is written on the next daily run."
        : "No brief has been written for this month.";
  }
}

function Findings({
  kind,
  items,
  currency,
}: {
  kind: "highlight" | "lowlight";
  items: RevenueFinding[];
  currency: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="pfindings">
      {items.map((f, i) => (
        <div key={f.id ?? i} className={`finding ${kind}`}>
          <div className="titlerow">
            <b>{f.title}</b>
          </div>
          <p>{f.detail}</p>
          <div className="pev">
            {f.evidence.map((r, j) => (
              <Evidence key={j} r={r} currency={currency} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** One cited fact, from the numbers the server copied in. */
function Evidence({ r, currency }: { r: RevenueRef; currency: string }) {
  if (!r.resolved) {
    return (
      <span className="ev" title="This reference names nothing in the digest">
        <span className="cid">{r.fact}</span> (unresolved)
      </span>
    );
  }
  return (
    <span className="ev">
      {r.label}: <b>{show(r, "value", currency)}</b>
      {hasPrior(r) && <> vs {show(r, "prior", currency)}</>}
    </span>
  );
}

function hasPrior(f: RevenueRef | RevenueFact): boolean {
  return f.unit === "label" ? !!f.prior_text : f.prior !== undefined && f.prior !== null;
}

function show(f: RevenueRef | RevenueFact, which: "value" | "prior", currency: string): string {
  if (f.unit === "label") return (which === "value" ? f.text : f.prior_text) ?? "—";
  const v = f[which];
  if (v === undefined || v === null) return "—";
  const n = Number(v);
  switch (f.unit) {
    case "money":
      return money(v, currency);
    case "share":
      return `${(n * 100).toFixed(1)}%`;
    case "minutes":
      return `${Math.round(n).toLocaleString("en-IN")} min`;
    default:
      return n.toLocaleString("en-IN");
  }
}

/** The digest's own lines: what moved, most severe first. */
function Signals({ data }: { data: CustomerRevenueBrief }) {
  const { digest } = data;
  const byId = new Map(digest.facts.map((f) => [f.id, f]));
  return (
    <ul className="rev-signals">
      {digest.signals.map((s, i) => (
        <li key={i} className={`sev-${s.severity}`}>
          <span className={`pill rev-sev ${s.severity}`}>{s.severity}</span> {s.describe}
          {s.facts
            .map((id) => byId.get(id))
            .filter((f): f is RevenueFact => !!f && f.unit === "money" && hasPrior(f))
            .slice(0, 1)
            .map((f) => (
              <span key={f.id} className="dim">
                {" "}
                ({show(f, "prior", digest.currency)} → {show(f, "value", digest.currency)})
              </span>
            ))}
        </li>
      ))}
    </ul>
  );
}
